import { useCallback, useMemo, useState } from 'react';
import { ProductSelections } from 'src/types/catalog';

let resolveCallback = (b: boolean) => {};

interface UseUnsavedSelectionOptions {
  productSelections: ProductSelections;
  onAddToCart: () => Promise<void>;
}

const useUnsavedSelection = ({ productSelections, onAddToCart }: UseUnsavedSelectionOptions) => {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingCallback, setPendingCallback] = useState<(() => void | Promise<void>) | null>(null);

  const getSelectedItemsCount = useCallback((): number => {
    if (Object.values(productSelections).length === 0) return 0;
    const selectedItems = Object.values(productSelections).filter((p) => p.selected);
    return selectedItems.length;
  }, [productSelections]);

  const hasSelections = useMemo(() => {
    if (Object.values(productSelections).length === 0) return false;
    return Object.values(productSelections).some((p) => p.selected);
  }, [productSelections]);

  const handleConfirm = useCallback(async () => {
    setIsOpen(false);
    if (pendingCallback) {
      await pendingCallback();
    }
    setPendingCallback(null);
    resolveCallback(true);
  }, [pendingCallback]);

  const handleAddToCart = useCallback(async () => {
    await onAddToCart();
    // Execute the pending callback to continue with the action
    if (pendingCallback) {
      await pendingCallback();
    }
    setIsOpen(false);
    setPendingCallback(null);
    resolveCallback(true);
  }, [onAddToCart, pendingCallback]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setPendingCallback(null);
    resolveCallback(false);
  }, []);

  const confirm = useCallback(
    async (callback: () => void | Promise<void>): Promise<boolean> => {
      // Check if there are selected items
      if (!hasSelections) {
        // Execute the callback immediately if no selections
        await callback();
        return true;
      }

      // Show dialog if there are selections
      setPendingCallback(() => callback);
      setIsOpen(true);
      return new Promise<boolean>((resolve) => {
        resolveCallback = resolve;
      });
    },
    [hasSelections]
  );

  return {
    isOpen,
    handleConfirm,
    handleAddToCart,
    handleClose,
    confirm,
    selectedItemsCount: getSelectedItemsCount(),
  };
};

export default useUnsavedSelection;
